import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Tag, Calendar } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { seo } from "../translations/seo";
import { useLanguage } from "../context/LanguageContext";
import { localizePost, DATE_LOCALES } from "../utils/blog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(isoStr, lang) {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleDateString(DATE_LOCALES[lang] || "fr-BE", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function BlogDetail() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  const view = post ? localizePost(post, language) : null;

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      {view && <Seo title={view.title} description={(view.excerpt || "").slice(0, 160)} image={view.image} />}
      {notFound && <Seo title={seo[language].notfound.title} description={seo[language].notfound.description} noindex />}

      {loading ? (
        <div className="pt-24 max-w-3xl mx-auto px-6 py-12 space-y-4" data-testid="blog-detail-loading">
          <div className="h-8 bg-slate-100 rounded-full w-3/4 animate-pulse" />
          <div className="h-64 bg-slate-100 rounded-3xl animate-pulse" />
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}
          </div>
        </div>
      ) : notFound ? (
        <div className="pt-32 max-w-xl mx-auto px-6 py-12 text-center" data-testid="blog-not-found">
          <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-4">{t.blog.not_found_title}</h1>
          <p className="text-slate-500 mb-8">{t.blog.not_found_text}</p>
          <Link
            to="/actualites"
            className="gradient-bg text-white font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> {t.blog.back}
          </Link>
        </div>
      ) : view ? (
        <article data-testid="blog-article">
          {/* Hero image */}
          {view.image && (
            <div className="pt-16 w-full h-72 sm:h-96 overflow-hidden">
              <img
                src={view.image}
                alt={view.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className={`max-w-3xl mx-auto px-6 sm:px-8 ${view.image ? "py-10" : "pt-28 pb-10"}`}>
            {/* Back link */}
            <Link
              to="/actualites"
              className="inline-flex items-center gap-2 text-[#2E4DB5] text-sm font-semibold hover:opacity-70 transition-opacity mb-8"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> {t.blog.back}
            </Link>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="flex items-center gap-1.5">
                <Tag className="w-4 h-4 text-[#3B9DA5]" />
                <span className="text-[#2E4DB5] text-sm font-semibold uppercase tracking-wide">
                  {view.category}
                </span>
              </div>
              <span className="text-slate-300">•</span>
              <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                <Calendar className="w-4 h-4" />
                {formatDate(view.published_at, language)}
              </div>
            </div>

            {/* Title */}
            <h1
              className="font-outfit text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-8"
              data-testid="blog-title"
            >
              {view.title}
            </h1>

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-[#2E4DB5] via-[#3B9DA5] to-transparent mb-8" />

            {/* Content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: view.content }}
              data-testid="blog-content"
            />

            {/* Footer nav */}
            <div className="mt-12 pt-6 border-t border-slate-100">
              <Link
                to="/actualites"
                className="inline-flex items-center gap-2 text-[#2E4DB5] font-semibold hover:opacity-70 transition-opacity"
              >
                <ArrowLeft className="w-4 h-4" /> {t.blog.back}
              </Link>
            </div>
          </div>
        </article>
      ) : null}

      <Footer />
    </div>
  );
}
